import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { Box, Divider, Typography } from "@mui/material";

import { TemplateWrapper } from "./styles";
import {
	IGallery,
	INVITATION_OCCASION,
	INVITATION_TYPES,
	Invitations,
	LangType
} from "../../../../store/services/invitations/dto/invitations.dto";
import { LANG_AR, LANG_EN, RTL } from "../constants";
import langEN from "../lang.EN.json";
import langAR from "../lang.AR.json";
import { getFieldMap } from "../../../../utils/inputs";
import { getWeddingDate } from "../../../../utils/formatDate";
import SaveTheDateBtn from "../Components/SaveTheDateBtn";
import { stem, stem02 } from "./constants";
import CountDown from "../CountDown";
import AddIcon from "@mui/icons-material/Add";
import InvitationGallery from "../../../InvitationGallery";
import Comments from "../Components/Comments";
import MapComponent from "../Components/MapComponent/MapComponent";
import MapButton from "../Components/MapButton";
import { TemplateColorObject } from "../../../../InvitationColorMatrix";
import PartnersSocialList from "../Components/PartnersSocialList";
import Footer from "../Components/Footer";

const Template03 = forwardRef(
	(
		{
			data,
			gallery,
			canEdit = false,
			colorMatrix
		}: {
			data: Invitations;
			gallery: IGallery[];
			colorMatrix: TemplateColorObject;
			canEdit?: boolean;
			setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
		},
		ref
	) => {
		const isTemplate = data?.type === INVITATION_TYPES.TEMPLATE;
		const invitationId = data?._id || "";
		const lang = data?.language === LANG_EN ? langEN : langAR;
		const fieldMap = getFieldMap(invitationId);
		const commentsSectionRef = useRef<HTMLElement>(null);
		const locationSectionRef = useRef<HTMLElement>(null);

		useImperativeHandle(ref, () => ({
			scrollToComments: () => {
				commentsSectionRef.current && commentsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			},
			scrollToLocation: () => {
				locationSectionRef.current && locationSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			}
		}));

		const invitation_getting_text = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.GETTING_ENGAGED;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.GETTING_KATB_KTAB;
				}
				default: {
					return lang.GETTING_MARRIED;
				}
			}
		}, [data]);
		const invitation_location_header = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.engagement_location;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.katb_ktab_location;
				}
				default: {
					return lang.wedding_location;
				}
			}
		}, [data]);

		return (
			<>
				<TemplateWrapper id={"template_wrapper"} colormatrix={colorMatrix}>
					{/*/////////// section 01 //////////////*/}
					<Box className={"section_01"}>
						{data?.has_images && (
							<img
								src={stem02}
								alt=""
								style={{ display: "none" }}
								id={data?.fieldVisibility?.main_image?.hidden ? "main_image" : ""}
							/>
						)}
						{data?.has_images && (canEdit || !data?.fieldVisibility?.main_image?.hidden) && (
							<Box className={"main_image_wrapper"}>
								<img
									src={data?.main_image}
									data-dispatch-id={fieldMap.MAIN_IMAGE}
									id={"main_image"}
									className={data?.fieldVisibility?.main_image?.hidden ? "shaded" : ""}
									alt="Couple"
								/>
								<Box className={"cloud"} />
							</Box>
						)}
						{!data?.has_images && <Box height={"40px"} />}
						<Box className={"section_content"}>
							<Box className={`save_date_wrapper ${data?.language === LANG_AR && RTL}`}>
								<Box display="flex" gap={1} alignItems={"center"} flexWrap={"wrap"}>
									<Typography data-dispatch-id={fieldMap.GROOM_NAME} className={"partners_text guide_edit_field"}>
										{data?.groom_name}
									</Typography>

									<Typography className={"partners_text"}> {lang.and} </Typography>

									<Typography data-dispatch-id={fieldMap.BRIDE_NAME} className={"partners_text"}>
										{data?.bride_name}
									</Typography>
								</Box>
								<Typography className={"date"} data-dispatch-id={fieldMap.WEDDING_DATE}>
									{getWeddingDate(data?.wedding_date, data?.language, data?.timezone, isTemplate)}
								</Typography>
								{(canEdit || !data?.fieldVisibility?.save_the_date_text?.hidden) && (
									<Typography
										data-dispatch-id={fieldMap.SAVE_THE_DATE}
										className={`info ${data?.fieldVisibility?.save_the_date_text?.hidden ? "shaded" : ""}`}
									>
										{data?.save_the_date_text}
									</Typography>
								)}

								<SaveTheDateBtn canEdit={canEdit} data={data!} btnText={lang.save_the_date_button_text} />
							</Box>
						</Box>
					</Box>
					{/*/////////// section 02 //////////////*/}
					<Box className={"section_02"}>
						<Typography className={"title"}>{invitation_getting_text}</Typography>
						<img src={stem02} alt="tree branch" />
						<Box className={"count_down_wrapper"}>
							<CountDown
								data={data || ({} as Invitations)}
								daysString={lang.days}
								hoursString={lang.hours}
								minutesString={lang.mins}
								secondsString={lang.sec}
							/>
						</Box>
					</Box>
					{/*/////////// section 03 //////////////*/}
					<Box className={"section_03"}>
						<Box className={"section_overlay"} />
						<Box className={"section_content"}>
							<Box className={"cloud"} />
							{data?.is_islamic && (
								<Typography className={"quran"}>
									بَارَكَ اللهُ لَكُما، وَبَارَكَ عَلَيْكُما، وَجَمَعَ بَيْنَكُما فِي خَيْرٍ
								</Typography>
							)}
							{(canEdit || !data?.fieldVisibility?.intro_text?.hidden) && (
								<Typography
									data-dispatch-id={fieldMap.INTRO_TEXT}
									className={`main_intro ${data?.fieldVisibility?.intro_text?.hidden ? "shaded" : ""} animate-on-scroll delay-1`}
								>
									{data?.intro_text}
								</Typography>
							)}
							{(canEdit || !data?.fieldVisibility?.sub_intro_text?.hidden) && (
								<Typography
									data-dispatch-id={fieldMap.SUB_INTRO}
									className={`main_sub_intro ${data?.fieldVisibility?.sub_intro_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
								>
									{data?.sub_intro_text}
								</Typography>
							)}
							<Box height={60} />
							{data?.has_images && (canEdit || !data?.fieldVisibility?.groom_image?.hidden) && (
								<Box
									className={`partner_frame_wrapper ${data?.fieldVisibility?.groom_image?.hidden ? "shaded" : ""}`}
									data-dispatch-id={fieldMap.GROOM_IMAGE}
								>
									<img src={data?.groom_image} className={"groom"} alt="groom" />
								</Box>
							)}

							<Typography data-dispatch-id={fieldMap.GROOM_NAME} className={"partner_name"}>
								{data?.groom_name}
							</Typography>

							<PartnersSocialList
								socials={data?.partner_socials_1 || []}
								canEdit={canEdit}
								dispatchId={fieldMap.SOCIAL_MEDIA_1}
							/>

							<Box height={10} />
							<Typography className={"and"}>&</Typography>
							<Box height={10} />
							{data?.has_images && (canEdit || !data?.fieldVisibility?.bride_image?.hidden) && (
								<Box
									data-dispatch-id={fieldMap.BRIDE_IMAGE}
									className={`partner_frame_wrapper ${data?.fieldVisibility?.bride_image?.hidden ? "shaded" : ""}`}
								>
									<img src={data?.bride_image} className={"bride"} alt="bride" />
								</Box>
							)}

							<Typography data-dispatch-id={fieldMap.BRIDE_NAME} className={"partner_name"}>
								{" "}
								{data?.bride_name}
							</Typography>

							<PartnersSocialList
								socials={data?.partner_socials_2 || []}
								canEdit={canEdit}
								dispatchId={fieldMap.SOCIAL_MEDIA_2}
							/>
						</Box>
					</Box>
					{/*/////////// section 04 //////////////*/}
					{data?.is_islamic && (
						<Box className={"section_04"}>
							<Typography className={"quran_start"}>بِسْمِ اللهِ الرَّحْمَنِ الرَّحِيمِِِ</Typography>
							<Typography className={"quran"}>
								﴿ وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُم مِّنْ أَنفُسِكُمْ أَزْوَاجًا لِّتَسْكُنُوا إِلَيْهَا وَجَعَلَ
								بَيْنَكُم مَّوَدَّةً وَرَحْمَةً ۚ إِنَّ فِي ذَٰلِكَ لَآيَاتٍ لِّقَوْمٍ يَتَفَكَّرُونَ ﴾
							</Typography>
							<Typography className={"quran_end"}>صَدَقَ اللهُ العَظِيمُ</Typography>
						</Box>
					)}

					{/*/////////// section 05 //////////////*/}
					{!data?.fieldVisibility?.loveStory?.hidden && (
						<Box className={"section_05"}>
							{data?.has_images && (canEdit || !data?.fieldVisibility?.story_image?.hidden) && (
								<Box className={"image_wrapper"}>
									<Box className={"cloudUp"} />
									<Box className={`love_story_wrapper ${data?.fieldVisibility?.story_image?.hidden ? "shaded" : ""}`}>
										<img
											src={data?.story_image}
											data-dispatch-id={fieldMap.STORY_IMAGE}
											className="story_image"
											alt="Love Story"
										/>
									</Box>
									<Box className={"cloudDown"} />
								</Box>
							)}

							<Typography className={"section_title"}>{lang.our_love_story}</Typography>
							{data?.story.length > 0 && (
								<Box className={"stories"} data-dispatch-id={fieldMap.STORY}>
									{data?.story.map((story, index) => (
										<Box key={story._id} className={`story animate-on-scroll delay-${index + 1}`}>
											<Typography>{story.title}</Typography>
											<Divider />
											<Typography>{story.description}</Typography>
										</Box>
									))}
								</Box>
							)}
							{canEdit && (data?.story || []).length === 0 && (
								<Box my={3}>
									<Box className={"add_story"} data-dispatch-id={fieldMap.STORY}>
										<AddIcon data-id={`${fieldMap.STORY}-add`} />
									</Box>
								</Box>
							)}
						</Box>
					)}

					{/*/////////// section 06 //////////////*/}
					{!data?.fieldVisibility?.gallery?.hidden && data?.has_images && (
						<Box className={"section_06"}>
							<Typography className={"section_title"}>{lang.our_gallery}</Typography>
							<img src={stem} className={"stem"} alt="flower stem" />
							<Box height={30}></Box>

							<Box data-dispatch-id={fieldMap.GALLERY}>
								<InvitationGallery gallery={gallery || []} canEdit={canEdit} />
							</Box>
						</Box>
					)}
					{/*/////////// section 07 //////////////*/}
					{!data?.fieldVisibility?.comments?.hidden && (
						<Box className={"section_07"} ref={commentsSectionRef}>
							<Box className={"section_overlay"} />
							<Box className={"section_content"}>
								{!data?.fieldVisibility?.gallery?.hidden && <Box height={30} />}
								<Typography className={"section_title"}>{lang.comments}</Typography>
								<img src={stem} className={"stem"} alt="flower stem" />
								<Box height={30}></Box>

								{(canEdit || !data?.fieldVisibility?.comments_text?.hidden) && (
									<>
										<Typography
											data-dispatch-id={fieldMap.COMMENTS_TEXT}
											className={`comment_text ${data?.fieldVisibility?.comments_text?.hidden ? "shaded" : ""} animate-on-scroll delay-1`}
										>
											{data?.comments_text}
										</Typography>
										<Box height={30}></Box>
									</>
								)}
								{(canEdit || !data?.fieldVisibility?.comments_sub_text?.hidden) && (
									<>
										<Typography
											data-dispatch-id={fieldMap.COMMENTS_SUB_TEXT}
											className={`comment_sub_text ${data?.fieldVisibility?.comments_sub_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
										>
											{data?.comments_sub_text}
										</Typography>
										<Box height={30}></Box>
									</>
								)}

								<Comments canEdit={canEdit} language={data?.language as LangType} colormatrix={colorMatrix} />
							</Box>
						</Box>
					)}
					<Box className={"section_08"} ref={locationSectionRef}>
						{!data?.fieldVisibility?.location?.hidden && (
							<>
								<Typography className={"section_title"}>{invitation_location_header}</Typography>
								<img src={stem} className={"stem"} alt="flower stem" />
								<Box height={20} />
								{(canEdit || !data?.fieldVisibility?.wedding_location_text?.hidden) && (
									<Typography
										data-dispatch-id={fieldMap.LOCATION_TEXT}
										className={`location_text ${data?.fieldVisibility?.wedding_location_text?.hidden ? "shaded" : ""}`}
									>
										{data?.wedding_location_text}
									</Typography>
								)}
								<Box height={20} />
								<Box data-dispatch-id={fieldMap.MAP}>
									<MapComponent canEdit={canEdit} />
								</Box>

								<Box height={20} />

								<Box data-dispatch-id={fieldMap.MAP}>
									<MapButton canEdit={canEdit} text={lang.location} />
								</Box>
							</>
						)}

						<Box height={30} />
						{data?.has_images && (canEdit || !data?.fieldVisibility?.main_image?.hidden) && (
							<Box
								className={`partner_04_wrapper ${data?.fieldVisibility?.main_image?.hidden ? "shaded" : ""}`}
								data-dispatch-id={fieldMap.MAIN_IMAGE}
							>
								<img src={data?.main_image} alt="couple" className={"partner_04"} />
							</Box>
						)}

						<Box
							my={3}
							display="flex"
							gap={1}
							alignItems={"center"}
							className={`${data?.language === LANG_AR && RTL} animate-on-scroll delay-1`}
							flexWrap={"wrap"}
						>
							<Typography data-dispatch-id={fieldMap.GROOM_NAME} className={"partners_text"}>
								{data?.groom_name}
							</Typography>

							<Typography className={"partners_text"}> {lang.and} </Typography>

							<Typography data-dispatch-id={fieldMap.BRIDE_NAME} className={"partners_text"}>
								{data?.bride_name}
							</Typography>
						</Box>
						{(canEdit || !data?.fieldVisibility?.conclusion_text?.hidden) && (
							<Typography
								data-dispatch-id={fieldMap.CONCLUSION_TEXT}
								className={`conclusion_text ${data?.fieldVisibility?.conclusion_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
							>
								{data?.conclusion_text}
							</Typography>
						)}
					</Box>
					<Footer colormatrix={colorMatrix} />
				</TemplateWrapper>
			</>
		);
	}
);

export default Template03;
